import api from '../../utils/api';

export type BlogCategoryApproval = 'pending' | 'approved' | 'rejected';

export interface BlogCategory {
  categoryId: string;
  name: string;
  createdBy?: 'admin' | 'doctor';
  vendorId?: string | null;
  approvalStatus?: BlogCategoryApproval;
  approvalReason?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCategoryRequest {
  name: string;
}

export interface UpdateCategoryRequest {
  name?: string;
  approvalStatus?: BlogCategoryApproval;
  approvalReason?: string | null;
}

export type GetCategoriesOptions = {
  /** When true with createdBy=admin, returns approved + pending admin categories */
  includeAll?: boolean;
  createdBy?: 'admin' | 'doctor';
};

/** Approved categories for blog create/edit/filter pickers */
export const getCategories = async (
  options: GetCategoriesOptions = {}
): Promise<BlogCategory[]> => {
  const params: Record<string, string> = {};
  if (options.includeAll) params.includeAll = 'true';
  if (options.createdBy) params.createdBy = options.createdBy;

  const response = await api.get('/blog-categories/categories', { params });
  return Array.isArray(response.data) ? response.data : [];
};

/** Admin manage page: admin-created approved + pending */
export const getAdminManagedCategories = async (): Promise<BlogCategory[]> => {
  return getCategories({ includeAll: true, createdBy: 'admin' });
};

// Create a new category (backend auto-approves admin categories)
export const createCategory = async (data: CreateCategoryRequest): Promise<BlogCategory> => {
  const response = await api.post('/blog-categories/categories', {
    name: data.name,
    createdBy: 'admin',
  });
  return response.data.category;
};

// Update a category
export const updateCategory = async (
  id: string,
  data: UpdateCategoryRequest
): Promise<BlogCategory> => {
  const response = await api.put(`/blog-categories/categories/${id}`, data);
  return response.data.category;
};

export const approveCategory = async (id: string): Promise<BlogCategory> => {
  return updateCategory(id, { approvalStatus: 'approved' });
};

export const markCategoryPending = async (id: string): Promise<BlogCategory> => {
  return updateCategory(id, { approvalStatus: 'pending', approvalReason: null });
};

// Delete a category
export const deleteCategory = async (id: string): Promise<void> => {
  await api.delete(`/blog-categories/categories/${id}`);
};
